import { AWBStatus, InvoiceType, KYCType, PaymentMethod } from '../types/awb';

import { getApiClient } from './apiClient';

// AWB interfaces matching backend
export interface ShipperConsigneeData {
    searchAddressBook?: string;
    code?: string;
    company?: string;
    personName?: string;
    address1: string;
    address2?: string;
    address3?: string;
    postZipCode?: string;
    city: string;
    stateCounty: string;
    country: string;
    phoneNumber: string;
    mobileNumber?: string;
    emailAddress?: string;
    kycType?: KYCType;
    kycNumber?: string;
    kycUpload?: string;
    kycDocuments?: any[];
}

export interface WeightDimensionData {
    parcelNo?: string;
    boxNo: number;
    actualWeight: number;
    length: number;
    breadth: number;
    height: number;
    volumetricWeight?: number;
    chargeableWeight?: number;
}

export interface ShipmentInvoiceLineItem {
    boxNo: string;
    srNo: number;
    description: string;
    hsCode?: string;
    unitType: string;
    quantity: number;
    unitWeight: number;
    igst: number;
    unitRate: number;
    amount?: number;
}

export interface ShipmentInvoiceData {
    invoiceType: InvoiceType;
    currency: string;
    incoterms?: string;
    note?: string;
    remarks?: string;
    lineItems: ShipmentInvoiceLineItem[];
    totalAmount?: number;
    /**
     * Some parts of the UI/PDF generator expect `totalValue`.
     * Keep as an alias to avoid runtime changes.
     */
    totalValue?: number;
    totalWeight?: number;
    totalQuantity?: number;
}

export interface CreateAWBRequest {
    // Basic Information
    customer: string;
    company: string;
    origin?: string;
    destination: string;
    product: string;
    bookingDate: Date;
    bookingTime: string;
    service: string;
    vendor?: string;
    forwardingNumber?: string;
    forwardingNumber2?: string;
    referenceNumber?: string;
    referenceName?: string;
    shipmentValue: number;
    currency: string;
    invoiceDate?: Date;
    invoiceNumber?: string;
    remarks?: string;
    paymentMethod: PaymentMethod;
    originHub?: string;

    // Shipper Information
    shipper: ShipperConsigneeData;

    // Consignee Information
    consignee: ShipperConsigneeData;

    // Weight and Dimensions
    totalPieces: number;
    consignorWeight?: number;
    addWeight?: number;
    weightDimensions: WeightDimensionData[];

    // Optional Shipment Invoice
    shipmentInvoice?: ShipmentInvoiceData;

    // Delivery Information
    expectedDeliveryDate?: Date;
    expectedDeliveryTime?: string;
    deliveryDate?: Date;
    deliveryTime?: string;
    connectionDate?: Date;
    connectionTime?: string;
    appointmentDate?: Date;
    appointmentTime?: string;
    podUploadedDate?: Date;
    podUploadedTime?: string;
    deliveryCost?: number;
    receiverName?: string;
    receiverMobile?: string;
    receiverEmail?: string;
    deliveryRemarks?: string;
    awbStatusCode?: string;
    awbStatusName?: string;
    reasonForStatus?: string;
    codAmount?: number;
    codAmountCollected?: number;
    podHardCopy?: boolean;
}

export interface UpdateAWBRequest extends Partial<CreateAWBRequest> {
    awbNumber: string;
}

export interface SearchAWBRequest {
    awbNumber?: string;
    customer?: string;
    company?: string;
    service?: string;
    vendor?: string;
    currentStatus?: AWBStatus;
    destination?: string;
    paymentMethod?: PaymentMethod;
    weightMin?: number;
    weightMax?: number;
    bookingDateFrom?: Date;
    bookingDateTo?: Date;
    shipperPhone?: string;
    consigneePhone?: string;
    referenceNumber?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    responseMode?: 'summary' | 'full';
}

export interface TrackingEvent {
    id: string;
    eventDate: string;
    eventTime: string;
    eventDescription: string;
    eventLocation: string;
    eventType: 'MANUAL' | 'API';
    eventState: string;
    remarks?: string;
    changes?: {
        field: string;
        oldValue: any;
        newValue: any;
    }[];
    createdBy: string; // This might be an object populated by backend, need to check usage
    // Checking usage in AWBViewPageNew.tsx: log.createdBy?.name || 'Unknown User'
    // This suggests createdBy is an object in the component usage.
    // However, interface says string. This might be an existing inconsistency or createdBy is populated.
    // Let's stick to the existing pattern but add changes. 
    // Wait, the backend populate call populates createdBy. So it should probably be `any` or `{ name: string, email: string } | string`.
    // But for now I am just adding `changes`.
    createdAt: string;
}

export interface AWBComment {
    id: string;
    comment: string;
    attachments?: string[];
    createdBy: string;
    createdAt: string;
}

export interface AWBAttachment {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
    uploadDate: string;
    category: string;
}

export interface AWBDetail {
    id: string;
    awbNumber: string;
    customer: any;
    company: any;
    origin?: any;
    destination: any;
    product: any;
    bookingDate: string;
    bookingTime: string;
    service: any;
    vendor?: any;
    forwardingNumber?: string;
    forwardingNumber2?: string;
    referenceNumber?: string;
    referenceName?: string;
    shipmentValue: number;
    currency: any;
    invoiceDate?: string;
    invoiceNumber?: string;
    content?: string;
    contentDescription?: string;
    declaredValue?: number;
    ewayBillNumber?: string;
    rateContract?: string;
    cftContract?: string;
    tat?: string;
    remarks?: string;
    paymentMethod: PaymentMethod;
    originHub?: any;
    currentStatus: AWBStatus;
    status: AWBStatus; // Add status property for compatibility

    shipper: ShipperConsigneeData;
    consignee: ShipperConsigneeData;

    totalPieces: number;
    totalActualWeight: number;
    totalVolumetricWeight: number;
    consignorWeight?: number;
    addWeight?: number;
    totalChargeableWeight: number;
    weightDimensions: WeightDimensionData[];

    shipmentInvoice?: ShipmentInvoiceData;

    // Delivery Information
    expectedDeliveryDate?: string;
    expectedDeliveryTime?: string;
    actualTat?: number;
    crossedEddDays?: number;
    deliveryDate?: string;
    deliveryTime?: string;
    apiCrossedEddDays?: number;
    connectionDate?: string;
    connectionTime?: string;
    appointmentDate?: string;
    appointmentTime?: string;
    podUploadedDate?: string;
    podUploadedTime?: string;
    deliveryCost?: number;
    receiverName?: string;
    receiverMobile?: string;
    receiverEmail?: string;
    deliveryRemarks?: string;
    awbStatusCode?: string;
    awbStatusName?: string;
    reasonForStatus?: string;
    codAmount?: number;
    codAmountCollected?: number;
    podHardCopy?: boolean;
    totalPcs?: number;
    totalPickupScanParcels?: number;
    totalInscanParcels?: number;

    trackingEvents: TrackingEvent[];
    comments: AWBComment[];
    attachments: AWBAttachment[];

    createdAt: string;
    updatedAt: string;
}

export interface AWBSearchResult {
    awbs: AWBDetail[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface AWBStats {
    totalAWBs: number;
    statusCounts: { [key in AWBStatus]: number };
    totalWeight: number;
    totalValue: number;
}

export interface AddTrackingEventRequest {
    eventDate: Date;
    eventTime: string;
    eventDescription: string;
    eventLocation: string;
    eventType: 'MANUAL' | 'API';
    eventState: string;
    remarks?: string;
}

export interface AddCommentRequest {
    comment: string;
    attachments?: string[];
}

export interface UpdateStatusRequest {
    newStatus: AWBStatus;
    eventDescription: string;
    eventLocation: string;
    remarks?: string;
}

class AWBService {
    private axiosInstance = getApiClient({ useWindowInstance: true });

    // Helper function to sanitize empty strings to undefined for optional ObjectId fields
    private sanitizeObjectIdFields(data: any): any {
        const sanitizedData = { ...data };
        const optionalFields = ['vendor', 'originHub', 'origin', 'forwardingNumber', 'forwardingNumber2', 'referenceNumber', 'referenceName', 'invoiceNumber', 'remarks'];

        console.log('AWB Service - Sanitizing data, currency before:', sanitizedData.currency);

        for (const field of optionalFields) {
            if (sanitizedData[field] === '') {
                sanitizedData[field] = undefined;
            }
        }

        // Ensure required fields like currency are not accidentally removed
        const requiredFields = ['currency', 'customer', 'company', 'destination', 'product', 'service'];
        for (const field of requiredFields) {
            if (sanitizedData[field] === '' || sanitizedData[field] === null) {
                console.warn(`AWB Service - Required field ${field} is empty:`, sanitizedData[field]);
            }
        }

        console.log('AWB Service - Sanitizing data, currency after:', sanitizedData.currency);

        // Also handle nested objects
        if (sanitizedData.shipper) {
            const shipperOptionalFields = ['searchAddressBook', 'company', 'personName', 'address2', 'address3', 'postZipCode', 'emailAddress', 'kycType', 'kycNumber', 'kycUpload'];
            for (const field of shipperOptionalFields) {
                if (sanitizedData.shipper[field] === '') {
                    sanitizedData.shipper[field] = undefined;
                }
            }
            // Handle kycUpload - remove if it's a File object or empty object (should be uploaded separately)
            if (sanitizedData.shipper.kycUpload) {
                if (typeof sanitizedData.shipper.kycUpload === 'object') {
                    // If it's a File object or empty object, remove it
                    sanitizedData.shipper.kycUpload = undefined;
                } else if (typeof sanitizedData.shipper.kycUpload !== 'string' || sanitizedData.shipper.kycUpload.trim() === '') {
                    sanitizedData.shipper.kycUpload = undefined;
                }
            }
        }

        if (sanitizedData.consignee) {
            const consigneeOptionalFields = ['searchAddressBook', 'company', 'personName', 'address2', 'address3', 'postZipCode', 'emailAddress', 'kycType', 'kycNumber', 'kycUpload'];
            for (const field of consigneeOptionalFields) {
                if (sanitizedData.consignee[field] === '') {
                    sanitizedData.consignee[field] = undefined;
                }
            }
            // Handle kycUpload - remove if it's a File object or empty object (should be uploaded separately)
            if (sanitizedData.consignee.kycUpload) {
                if (typeof sanitizedData.consignee.kycUpload === 'object') {
                    // If it's a File object or empty object, remove it
                    sanitizedData.consignee.kycUpload = undefined;
                } else if (typeof sanitizedData.consignee.kycUpload !== 'string' || sanitizedData.consignee.kycUpload.trim() === '') {
                    sanitizedData.consignee.kycUpload = undefined;
                }
            }
        }

        return sanitizedData;
    }

    async createAWB(data: CreateAWBRequest): Promise<AWBDetail> {
        const sanitizedData = this.sanitizeObjectIdFields(data);
        try {
            const response = await this.axiosInstance.post('/api/awb', sanitizedData);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Create AWB Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async updateAWB(awbNumber: string, data: Partial<CreateAWBRequest>): Promise<AWBDetail> {
        console.log('AWB Service - Raw update data before sanitization:', data);
        console.log('AWB Service - Currency in raw data:', data.currency);

        const sanitizedData = this.sanitizeObjectIdFields(data);

        console.log('AWB Service - Sanitized update data:', sanitizedData);
        console.log('AWB Service - Currency in sanitized data:', sanitizedData.currency);

        try {
            const response = await this.axiosInstance.put(`/api/awb/${awbNumber}`, sanitizedData);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Update AWB Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async getAWB(awbNumber: string): Promise<AWBDetail> {
        try {
            const response = await this.axiosInstance.get(`/api/awb/${awbNumber}`);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Get AWB Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async searchAWBs(params: SearchAWBRequest): Promise<AWBSearchResult> {
        try {
            const queryParams = new URLSearchParams();

            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (value instanceof Date) {
                        queryParams.append(key, value.toISOString());
                    } else {
                        queryParams.append(key, value.toString());
                    }
                }
            });

            const response = await this.axiosInstance.get(`/api/awb/search?${queryParams}`);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Search AWBs Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async deleteAWB(awbNumber: string): Promise<void> {
        try {
            await this.axiosInstance.delete(`/api/awb/${awbNumber}`);
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Delete AWB Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async addTrackingEvent(awbNumber: string, data: AddTrackingEventRequest): Promise<AWBDetail> {
        try {
            const response = await this.axiosInstance.post(`/api/awb/${awbNumber}/tracking`, data);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Add Tracking Event Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async addComment(awbNumber: string, data: AddCommentRequest): Promise<AWBDetail> {
        try {
            const response = await this.axiosInstance.post(`/api/awb/${awbNumber}/comments`, data);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Add Comment Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async updateStatus(awbNumber: string, data: UpdateStatusRequest): Promise<AWBDetail> {
        try {
            const response = await this.axiosInstance.put(`/api/awb/${awbNumber}/status`, data);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Update Status Error:', error.response?.data || error.message);
            throw error;
        }
    }

    async generateLabel(awbNumber: string): Promise<Blob> {
        try {
            const response = await this.axiosInstance.get(`/api/awb/${awbNumber}/label`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Generate Label Error:', error.response?.data || error.message);
            throw error;
        }
    }

    // Utility methods for form data transformation
    formatFormDataForAPI(formData: any): CreateAWBRequest {
        return {
            customer: formData.customer,
            company: formData.company,
            origin: formData.origin,
            destination: formData.destination,
            product: formData.product,
            bookingDate: new Date(formData.bookingDate),
            bookingTime: formData.bookingTime,
            service: formData.service,
            vendor: formData.vendor,
            forwardingNumber: formData.forwardingNumber,
            forwardingNumber2: formData.forwardingNumber2,
            referenceNumber: formData.referenceNumber,
            referenceName: formData.referenceName,
            shipmentValue: formData.shipmentValue,
            currency: formData.currency,
            invoiceDate: formData.invoiceDate ? new Date(formData.invoiceDate) : undefined,
            invoiceNumber: formData.invoiceNumber,
            remarks: formData.remarks,
            paymentMethod: formData.paymentMethod,
            originHub: formData.originHub,
            shipper: formData.shipper,
            consignee: formData.consignee,
            totalPieces: formData.totalPieces,
            consignorWeight: formData.consignorWeight,
            addWeight: formData.addWeight,
            weightDimensions: formData.weightDimensions,
            shipmentInvoice: formData.shipmentInvoice,

            // Delivery Information
            expectedDeliveryDate: formData.expectedDeliveryDate ? new Date(formData.expectedDeliveryDate) : undefined,
            expectedDeliveryTime: formData.expectedDeliveryTime,
            deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate) : undefined,
            deliveryTime: formData.deliveryTime,
            connectionDate: formData.connectionDate ? new Date(formData.connectionDate) : undefined,
            connectionTime: formData.connectionTime,
            appointmentDate: formData.appointmentDate ? new Date(formData.appointmentDate) : undefined,
            appointmentTime: formData.appointmentTime,
            podUploadedDate: formData.podUploadedDate ? new Date(formData.podUploadedDate) : undefined,
            podUploadedTime: formData.podUploadedTime,
            deliveryCost: formData.deliveryCost,
            receiverName: formData.receiverName,
            receiverMobile: formData.receiverMobile,
            receiverEmail: formData.receiverEmail,
            deliveryRemarks: formData.deliveryRemarks,
            awbStatusCode: formData.awbStatusCode,
            awbStatusName: formData.awbStatusName,
            reasonForStatus: formData.reasonForStatus,
            codAmount: formData.codAmount,
            codAmountCollected: formData.codAmountCollected,
            podHardCopy: formData.podHardCopy
        };
    }

    formatAWBForForm(apiData: AWBDetail): any {
        console.log('AWB Service - Raw API data for formatting:', apiData);

        // Helper function to extract ID from object or return string
        const extractId = (value: any) => {
            if (typeof value === 'object' && value !== null) {
                return value._id || value.id || value;
            }
            return value || '';
        };

        // Helper function to extract country ID for form
        const extractCountryId = (country: any) => {
            if (typeof country === 'object' && country !== null) {
                return country._id || country.id || country.code || country;
            }
            return country || '';
        };

        const formattedData = {
            // Basic Information
            awbNumber: apiData.awbNumber || '',
            customer: extractId(apiData.customer),
            company: extractId(apiData.company),
            origin: extractId(apiData.origin),
            destination: extractId(apiData.destination),
            product: extractId(apiData.product),
            bookingDate: apiData.bookingDate ? apiData.bookingDate.split('T')[0] : '',
            bookingTime: apiData.bookingTime || '',
            service: extractId(apiData.service),
            vendor: extractId(apiData.vendor),
            forwardingNumber: apiData.forwardingNumber || '',
            forwardingNumber2: apiData.forwardingNumber2 || '',
            referenceNumber: apiData.referenceNumber || '',
            referenceName: apiData.referenceName || '',
            shipmentValue: apiData.shipmentValue || 0,
            currency: (() => {
                const currencyId = extractId(apiData.currency);
                console.log('AWB Service - formatAWBForForm - Raw currency data:', apiData.currency);
                console.log('AWB Service - formatAWBForForm - Extracted currency ID:', currencyId);
                console.log('AWB Service - formatAWBForForm - Currency ID type:', typeof currencyId);
                return currencyId;
            })(),
            invoiceDate: apiData.invoiceDate ? apiData.invoiceDate.split('T')[0] : '',
            invoiceNumber: apiData.invoiceNumber || '',
            content: apiData.content || '',
            rateContract: apiData.rateContract || '',
            cftContract: apiData.cftContract || '',
            tat: apiData.tat || '',
            remarks: apiData.remarks || '',
            paymentMethod: apiData.paymentMethod || 'PREPAID',
            originHub: extractId(apiData.originHub),

            // Shipper Information
            shipper: {
                searchAddressBook: apiData.shipper?.searchAddressBook || '',
                code: apiData.shipper?.code || '',
                company: apiData.shipper?.company || '',
                personName: apiData.shipper?.personName || '',
                address1: apiData.shipper?.address1 || '',
                address2: apiData.shipper?.address2 || '',
                address3: apiData.shipper?.address3 || '',
                postZipCode: apiData.shipper?.postZipCode || '',
                city: apiData.shipper?.city || '',
                stateCounty: apiData.shipper?.stateCounty || '',
                country: extractCountryId(apiData.shipper?.country),
                phoneNumber: apiData.shipper?.phoneNumber || '',
                emailAddress: apiData.shipper?.emailAddress || '',
                kycType: apiData.shipper?.kycType || '',
                kycNumber: apiData.shipper?.kycNumber || '',
                kycUpload: apiData.shipper?.kycUpload || '',
                kycDocuments: apiData.shipper?.kycDocuments || []
            },

            // Consignee Information
            consignee: {
                searchAddressBook: apiData.consignee?.searchAddressBook || '',
                code: apiData.consignee?.code || '',
                company: apiData.consignee?.company || '',
                personName: apiData.consignee?.personName || '',
                address1: apiData.consignee?.address1 || '',
                address2: apiData.consignee?.address2 || '',
                address3: apiData.consignee?.address3 || '',
                postZipCode: apiData.consignee?.postZipCode || '',
                city: apiData.consignee?.city || '',
                stateCounty: apiData.consignee?.stateCounty || '',
                country: extractCountryId(apiData.consignee?.country),
                phoneNumber: apiData.consignee?.phoneNumber || '',
                emailAddress: apiData.consignee?.emailAddress || '',
                kycType: apiData.consignee?.kycType || '',
                kycNumber: apiData.consignee?.kycNumber || '',
                kycUpload: apiData.consignee?.kycUpload || '',
                kycDocuments: apiData.consignee?.kycDocuments || []
            },

            // Weight and Dimensions
            totalPieces: apiData.totalPieces || 1,
            consignorWeight: apiData.consignorWeight || 0,
            addWeight: apiData.addWeight || 0,
            weightDimensions: apiData.weightDimensions?.length > 0 ? apiData.weightDimensions : [{
                parcelNo: '',
                boxNo: 1,
                actualWeight: 0,
                length: 0,
                breadth: 0,
                height: 0,
                volumetricWeight: 0,
                chargeableWeight: 0
            }],

            // Shipment Invoice
            shipmentInvoice: apiData.shipmentInvoice || undefined,

            // Attachments (will be handled by AttachmentUploader component)
            attachments: []
        };

        console.log('AWB Service - Formatted data for form:', formattedData);
        console.log('AWB Service - Key field mappings:', {
            customer: `${apiData.customer} -> ${formattedData.customer}`,
            company: `${apiData.company} -> ${formattedData.company}`,
            destination: `${apiData.destination} -> ${formattedData.destination}`,
            service: `${apiData.service} -> ${formattedData.service}`,
            product: `${apiData.product} -> ${formattedData.product}`,
            currency: `${apiData.currency} -> ${formattedData.currency}`,
            shipperCountry: `${apiData.shipper?.country} -> ${formattedData.shipper.country}`,
            consigneeCountry: `${apiData.consignee?.country} -> ${formattedData.consignee.country}`
        });

        return formattedData;
    }

    async getAWBStats(params?: { startDate?: Date; endDate?: Date; status?: AWBStatus }): Promise<any> {
        try {
            const queryParams = new URLSearchParams();

            if (params?.startDate) {
                queryParams.append('startDate', params.startDate.toISOString());
            }
            if (params?.endDate) {
                queryParams.append('endDate', params.endDate.toISOString());
            }
            if (params?.status) {
                queryParams.append('status', params.status);
            }

            const response = await this.axiosInstance.get(`/api/awb/stats?${queryParams}`);
            return response.data.data;
        } catch (error: any) {
            // Preserve the original error structure for proper error handling
            console.error('AWB Service - Get AWB Stats Error:', error.response?.data || error.message);
            throw error;
        }
    }
}

// Export singleton instance
export const awbService = new AWBService();
export default awbService; 
